"use client";

import React, { Suspense, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { WebsiteAPI } from "../api/content-website";
import type {
  ApiResponse,
  HomepageContentResponse,
  KeywordsPageResponse,
} from "../entitites/KeywordsResponse";
import { DEFAULT_WEBSITE_DOMAIN } from "../config/website";
import { CasinoPageContext } from "../CasinoPageContext";

const img1 = new URL("@/app/assets/img1.jpg", import.meta.url).href;
const img2 = new URL("@/app/assets/img2.jpg", import.meta.url).href;
const img3 = new URL("@/app/assets/img3.jpg", import.meta.url).href;
const img4 = new URL("@/app/assets/img4.jpg", import.meta.url).href;
const img5 = new URL("@/app/assets/img5.jpg", import.meta.url).href;

const images = [img1, img2, img3, img4, img5];
const blogHero = images[Math.floor(Math.random() * images.length)];

function normalizeIndex(value: string | null): number | null {
  if (!value) return null;
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function sortBlogs(blogs: HomepageContentResponse["blogsSection"]["blogs"]) {
  return [...blogs].sort((a, b) => {
    const da = a.date ? new Date(a.date).getTime() : 0;
    const db = b.date ? new Date(b.date).getTime() : 0;
    if (Number.isNaN(da) || Number.isNaN(db)) return 0;
    return db - da;
  });
}

interface BlogDetailsContentProps {
  website: KeywordsPageResponse | null;
  loading: boolean;
}

const BlogDetailsContent: React.FC<BlogDetailsContentProps> = ({
  website,
  loading,
}) => {
  const searchParams = useSearchParams();
  const index = normalizeIndex(searchParams.get("id"));

  const homepage: HomepageContentResponse | null =
    (website?.homepageContent as HomepageContentResponse | null) ?? null;
  const blogs = homepage?.blogsSection?.blogs ?? [];
  const sortedBlogs = useMemo(() => sortBlogs(blogs), [blogs]);

  const blog =
    index !== null && index >= 0 && index < sortedBlogs.length
      ? sortedBlogs[index]
      : null;

  if (loading) {
    return (
      <main className="pt-24 pb-20" id="main-content">
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="h-12 w-12 rounded-full border-4 border-cyan-400/80 border-t-transparent animate-spin" />
            <p className="text-slate-300 text-sm">Loading blog details…</p>
          </div>
        </div>
      </main>
    );
  }

  if (!blog) {
    return (
      <main className="pt-24 pb-20" id="main-content">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold mb-4">
            Blog not found
          </h1>
          <p className="text-slate-300 text-sm sm:text-base mb-6">
            The blog post you tried to open could not be matched to the current
            content. Please go back to the blog listing and try another article.
          </p>
        </article>
      </main>
    );
  }

  return (
    <main className="pt-24 pb-20" id="main-content">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero image */}
        <div className="relative w-full h-52 sm:h-64 md:h-80 mb-6 overflow-hidden rounded-3xl border border-slate-700/70">
          <Image
            src={blogHero}
            alt={blog.title}
            fill
            sizes="(min-width: 1024px) 960px, 100vw"
            className="object-cover"
            priority
          />
        </div>

        {/* Meta */}
        <header className="mb-6 text-center sm:text-left">
          <p className="text-[11px] uppercase tracking-[0.25em] text-cyan-400/80 mb-2">
            {blog.category || "CasinoB8 Blog"}
          </p>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-3 leading-snug break-words">
            {blog.title}
          </h1>
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 text-[11px] sm:text-xs text-slate-300">
            <span className="px-3 py-1 rounded-full bg-slate-800/80 border border-slate-600/60">
              Posted by admin@hypegrow.io
            </span>
            {blog.date && (
              <span className="px-3 py-1 rounded-full bg-slate-800/80 border border-slate-600/60">
                Published on {blog.date}
              </span>
            )}
            <span className="px-3 py-1 rounded-full bg-slate-800/80 border border-slate-600/60">
              Reading time: ~3 min
            </span>
          </div>
        </header>

        {/* Body */}
        <section className="max-w-none">
          <p className="text-slate-100 text-sm sm:text-base leading-relaxed mb-4 whitespace-pre-line">
            {blog.excerpt}
          </p>
          <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
            This article is part of the CasinoB8 content experience, powered by
            Hypegrow. For more offers, verified brands, and sports betting
            options, you can explore platforms partnered with Casino24.
          </p>
        </section>
      </article>
    </main>
  );
};

const BlogDetailsPage = () => {
  const [website, setWebsite] = useState<KeywordsPageResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const response = await WebsiteAPI.getWebsiteByDomain(
          DEFAULT_WEBSITE_DOMAIN
        );

        if (cancelled) return;

        if (!response) {
          setWebsite(null);
          return;
        }

        const anyResponse: any = response;
        let data: KeywordsPageResponse | null = null;

        if (
          anyResponse &&
          typeof anyResponse === "object" &&
          "data" in anyResponse
        ) {
          const api = anyResponse as ApiResponse<KeywordsPageResponse>;
          data = api.data ?? null;
        } else {
          data = response as KeywordsPageResponse;
        }

        setWebsite(data);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <CasinoPageContext.Provider
        value={{
          domain: DEFAULT_WEBSITE_DOMAIN,
          website: website || undefined,
        }}
      >
        <Navbar />
        <Suspense
          fallback={
            <main className="pt-24 pb-20" id="main-content">
              <div className="min-h-[60vh] flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                  <div className="h-12 w-12 rounded-full border-4 border-cyan-400/80 border-t-transparent animate-spin" />
                  <p className="text-slate-300 text-sm">
                    Loading blog details…
                  </p>
                </div>
              </div>
            </main>
          }
        >
          <BlogDetailsContent website={website} loading={loading} />
        </Suspense>
        <Footer />
      </CasinoPageContext.Provider>
    </div>
  );
};

export default BlogDetailsPage;
